import { ArrowRight, FileText, Laptop, Server } from 'lucide-react';
import { useState } from 'react';

import { Button } from '../../button';
import { Input } from '../../input';

import { Lab, Panel, Reset, Result } from './lab-parts';
import { ModelSelect as SelectField } from './learning-model-surface';
import {
  addressFrame,
  connectionActions,
  connectionFrame,
  failureExamples,
  framingFrame,
  httpCommitFrame,
  outcomeExamples,
  ownershipFrame,
  privateAgreement,
  requestFrame,
  streamChunks,
  streamFrame,
  validationExamples,
  validationTrace,
  type FailureExample,
  type OutcomeExample,
  type ValidationExample,
} from './network-discovery-models';

import './network-discovery-labs.css';

import type { ReactNode } from 'react';

function Predict({ children }: { children: ReactNode }) {
  return (
    <p className="nd-predict">
      <strong>Predict first</strong> {children}
    </p>
  );
}
function Select({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
}) {
  return (
    <label>
      {label}
      <SelectField aria-label={label} value={value} options={options} onValueChange={onChange} />
    </label>
  );
}
function Output({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="nd-output" role="group" aria-label={label}>
      <small>{label}</small>
      <pre>{children}</pre>
    </div>
  );
}
function Progress({ labels, step }: { labels: readonly string[]; step: number }) {
  return (
    <ol className="nd-progress" aria-label="Completed operations">
      {labels.map((label, index) => (
        <li
          key={label}
          data-done={index < step}
          aria-current={index === step - 1 ? 'step' : undefined}
        >
          <span>{index < step ? 'Done' : 'Next'}</span>
          {label}
        </li>
      ))}
    </ol>
  );
}
function Records({ label, titles }: { label: string; titles: string[] | null }) {
  return (
    <div role="group" aria-label={label} className="nd-records">
      <strong>{label}</strong>
      {titles ? (
        <ul>
          {titles.map((title, index) => (
            <li key={title}>
              <small>#{index + 1}</small>
              {title}
            </li>
          ))}
        </ul>
      ) : (
        <p>Client sends requests; it does not own the task state.</p>
      )}
    </div>
  );
}
function Advance({
  children,
  onClick,
  disabled,
}: {
  children: ReactNode;
  onClick: () => void;
  disabled: boolean;
}) {
  return (
    <Button size="sm" variant="secondary" disabled={disabled} onClick={onClick}>
      {children}
      <ArrowRight size={13} aria-hidden="true" />
    </Button>
  );
}
const visible = (text: string) => text.replaceAll('\r', '␍').replaceAll('\n', '␊');

export function NetworksOwnershipLab() {
  const [owner, setOwner] = useState('separate');
  const [step, setStep] = useState(0);
  const single = owner === 'single';
  const frame = ownershipFrame(single, step);
  return (
    <Lab
      title="Two copies can disagree"
      eyebrow="Experiment · who owns the state?"
      className="nd-lab"
    >
      <Predict>
        Both processes started from the same file. Will the second save retain the first process’s
        new task?
      </Predict>
      <div className="cb-controls">
        <Select
          label="State design"
          value={owner}
          onChange={(value) => {
            setOwner(value);
            setStep(0);
          }}
          options={[
            { value: 'separate', label: 'Two independent writers' },
            { value: 'single', label: 'One owner accepts requests' },
          ]}
        />
      </div>
      <div className="nd-columns">
        <Panel label="Program A">
          <Laptop size={18} aria-hidden="true" />
          <Records label={single ? 'Client A' : 'A’s memory'} titles={frame.a} />
        </Panel>
        <Panel label="Program B">
          <Laptop size={18} aria-hidden="true" />
          <Records label={single ? 'Client B' : 'B’s memory'} titles={frame.b} />
        </Panel>
      </div>
      {single ? (
        <div className="nd-owner">
          <Server size={18} aria-hidden="true" />
          <Records label="Owner’s current data" titles={frame.owner} />
        </div>
      ) : null}
      <Panel label="Shared snapshot file">
        <FileText size={16} aria-hidden="true" />
        <Records label="Saved tasks" titles={frame.disk} />
      </Panel>
      <div className="cb-controls">
        <Advance disabled={step >= 2} onClick={() => setStep(step + 1)}>
          {step === 0 ? 'A creates Call Alex and saves' : 'B creates Book room and saves'}
        </Advance>
        <Reset
          onClick={() => {
            setOwner('separate');
            setStep(0);
          }}
        />
      </div>
      <Result tone={frame.lost ? 'warning' : 'neutral'}>
        {step === 0
          ? 'Identical source code and identical startup data do not create shared process memory.'
          : frame.lost
            ? 'Lost update: Book room survived, but Call Alex disappeared from the file. B saved its own older snapshot with one new task.'
            : single && step === 2
              ? 'Both requests passed through the owner’s latest state, so both tasks survive. The clients did not write their own snapshots.'
              : single
                ? 'A asked the owner to add a task. The owner changed and saved its state.'
                : 'A changed and saved its own memory. B still holds the startup snapshot.'}
      </Result>
    </Lab>
  );
}

export function NetworksAddressesLab() {
  const [origin, setOrigin] = useState('workspace');
  const [address, setAddress] = useState('127.0.0.1');
  const [port, setPort] = useState('8000');
  const [attempted, setAttempted] = useState(false);
  const frame = addressFrame(origin, address, port);
  const select = (setter: (value: string) => void) => (value: string) => {
    setter(value);
    setAttempted(false);
  };
  return (
    <Lab
      title="Same address, different destination"
      eyebrow="Experiment · find the listener"
      className="nd-lab"
      note="Match each process with its host and port to find the listening server."
    >
      <Predict>
        If the client moves to your laptop, does 127.0.0.1 still name the workspace?
      </Predict>
      <div className="cb-controls">
        <Select
          label="Client runs in"
          value={origin}
          onChange={select(setOrigin)}
          options={[
            { value: 'workspace', label: 'Workspace terminal' },
            { value: 'laptop', label: 'Laptop terminal' },
          ]}
        />
        <Select
          label="Destination address"
          value={address}
          onChange={select(setAddress)}
          options={[
            { value: '127.0.0.1', label: '127.0.0.1 · loopback' },
            { value: '10.0.0.8', label: '10.0.0.8 · workspace interface' },
          ]}
        />
        <Select
          label="Destination port"
          value={port}
          onChange={select(setPort)}
          options={[
            { value: '8000', label: '8000' },
            { value: '8001', label: '8001' },
          ]}
        />
      </div>
      <div className="nd-route" aria-label="Connection route">
        <span>
          <Laptop size={19} aria-hidden="true" />
          <strong>{origin === 'workspace' ? 'Workspace client' : 'Laptop client'}</strong>
          <small>Starts here</small>
        </span>
        <ArrowRight size={20} aria-hidden="true" />
        <span data-active={attempted}>
          <strong>
            {address}:{port}
          </strong>
          <small>Resolves inside {frame.destination}</small>
        </span>
      </div>
      <div className="nd-columns">
        <Panel label="Laptop network environment">
          <p>No listener in this example.</p>
        </Panel>
        <Panel label="Workspace network environment">
          <Server size={18} aria-hidden="true" />
          <p>
            Listener bound to <code>127.0.0.1:8000</code>
          </p>
          <p>It accepts connections addressed to workspace loopback only.</p>
        </Panel>
      </div>
      <div className="cb-controls">
        <Advance disabled={false} onClick={() => setAttempted(true)}>
          Trace this connection
        </Advance>
        <Reset
          onClick={() => {
            setOrigin('workspace');
            setAddress('127.0.0.1');
            setPort('8000');
            setAttempted(false);
          }}
        />
      </div>
      <Result tone={attempted ? (frame.connected ? 'success' : 'warning') : 'neutral'}>
        {attempted
          ? `${frame.connected ? 'Connected. ' : 'No matching listener. '}${frame.explanation}`
          : 'Choose a destination, then trace which environment and socket it names.'}
      </Result>
    </Lab>
  );
}

export function NetworksConnectionLab() {
  const [step, setStep] = useState(0);
  const frame = connectionFrame(step);
  return (
    <Lab
      title="One listener, one conversation"
      eyebrow="Experiment · socket lifetime"
      className="nd-lab"
    >
      <Predict>When this client disconnects, must the listening socket close too?</Predict>
      <Progress labels={connectionActions} step={step} />
      <div className="nd-listener">
        <Server size={19} aria-hidden="true" />
        <div>
          <strong>Listening socket</strong>
          <p>{frame.listener}</p>
        </div>
      </div>
      <div className="nd-columns">
        <Panel label="Client process">
          <Output label="Client socket">{frame.client}</Output>
          <Output label="Reply read by client">
            {frame.reply ? visible(frame.reply) : '(none yet)'}
          </Output>
        </Panel>
        <Panel label="Server process">
          <Output label="Conversation socket">{frame.server}</Output>
          <Output label="Request bytes sent">
            {frame.request ? visible(frame.request) : '(none yet)'}
          </Output>
        </Panel>
      </div>
      <p className="nd-caption">
        Each step shows the state after the named operation. ␊ represents a newline.
      </p>
      <div className="cb-controls">
        <Advance disabled={step >= connectionActions.length} onClick={() => setStep(step + 1)}>
          {connectionActions[step] ?? 'Conversation complete'}
        </Advance>
        <Reset onClick={() => setStep(0)} />
      </div>
      <Result>{frame.explanation}</Result>
    </Lab>
  );
}

export function NetworksStreamLab() {
  const [pattern, setPattern] = useState('split');
  const [rule, setRule] = useState('receive');
  const [step, setStep] = useState(0);
  const chunks = streamChunks[pattern] ?? [];
  const frame = streamFrame(chunks, step, rule === 'newline');
  const select = (setter: (value: string) => void) => (value: string) => {
    setter(value);
    setStep(0);
  };
  return (
    <Lab
      title="Where does one command end?"
      eyebrow="Experiment · bytes and boundaries"
      className="nd-lab"
      note="A read can return part of a message or several messages together. This protocol uses a newline to mark each message boundary."
    >
      <Predict>
        The sender wrote two complete commands and part of another. How many complete commands will
        each receiver find?
      </Predict>
      <div className="cb-controls">
        <Select
          label="Receive chunks"
          value={pattern}
          onChange={select(setPattern)}
          options={[
            { value: 'split', label: 'Split across commands' },
            { value: 'together', label: 'Everything arrives together' },
            { value: 'tiny', label: 'Several small chunks' },
          ]}
        />
        <Select
          label="Receiver rule"
          value={rule}
          onChange={select(setRule)}
          options={[
            { value: 'receive', label: 'One receive = one command' },
            { value: 'newline', label: 'Buffer until each newline' },
          ]}
        />
      </div>
      <Output label="Sender’s ordered bytes">CREATE|Read notes␊LIST␊PAR</Output>
      <div className="nd-chunks" aria-label="Receive chunks">
        {chunks.map((chunk, index) => (
          <span key={index} data-delivered={index < step}>
            <small>
              Receive {index + 1}
              {index < step ? ' · delivered' : ''}
            </small>
            <code>{visible(chunk)}</code>
          </span>
        ))}
      </div>
      <div className="nd-columns">
        <Panel label="Commands handed to the application">
          <ol className="nd-messages">
            {frame.messages.map((message, index) => (
              <li key={index}>
                <code>{visible(message)}</code>
              </li>
            ))}
          </ol>
          {!frame.messages.length ? <p>None yet</p> : null}
        </Panel>
        <Panel label="Receiver buffer">
          <Output label="Bytes waiting for a boundary">
            {frame.remainder ? visible(frame.remainder) : '(empty)'}
          </Output>
        </Panel>
      </div>
      <div className="cb-controls">
        <Advance disabled={step >= chunks.length} onClick={() => setStep(step + 1)}>
          Deliver next receive chunk
        </Advance>
        <Reset
          onClick={() => {
            setPattern('split');
            setRule('receive');
            setStep(0);
          }}
        />
      </div>
      <Result tone={step && rule === 'receive' ? 'warning' : 'neutral'}>
        {!step
          ? 'Change the receive boundaries without changing the sender’s bytes.'
          : rule === 'receive'
            ? 'The application is treating delivery boundaries as command boundaries. It may receive fragments or several commands as one “command”.'
            : `The receiver found ${frame.messages.length} complete command${frame.messages.length === 1 ? '' : 's'}. ${frame.remainder ? `It retains ${JSON.stringify(frame.remainder)} until a later newline arrives.` : 'No incomplete bytes remain.'}`}
      </Result>
    </Lab>
  );
}

export function NetworksFailuresLab() {
  const [example, setExample] = useState<FailureExample>('incomplete');
  const [step, setStep] = useState(0);
  const entry = failureExamples[example];
  return (
    <Lab
      title="A failed client need not end the service"
      eyebrow="Experiment · contain one conversation"
      className="nd-lab"
    >
      <Predict>
        Which resource should close after this failure: the current connection or the listening
        socket?
      </Predict>
      <div className="cb-controls">
        <Select
          label="First client’s behavior"
          value={example}
          onChange={(value) => {
            setExample(value as FailureExample);
            setStep(0);
          }}
          options={[
            { value: 'incomplete', label: 'Closes before the newline' },
            { value: 'silent', label: 'Connects but stays silent' },
            { value: 'oversized', label: 'Exceeds the buffer limit' },
            { value: 'malformed', label: 'Sends an unknown command' },
          ]}
        />
      </div>
      <div className="nd-listener">
        <Server size={18} aria-hidden="true" />
        <strong>Listening socket: open throughout</strong>
      </div>
      <div className="nd-columns">
        <Panel label="First conversation">
          <Output label="Input from first client">{visible(entry.input)}</Output>
          <p>{step ? entry.result : 'Waiting to inspect this connection.'}</p>
          <strong>{step ? 'This connection is closed.' : 'This connection is open.'}</strong>
        </Panel>
        <Panel label="Next conversation">
          <Output label="Input from next client">
            {step === 2 ? 'LIST␊' : '(not connected yet)'}
          </Output>
          <p>
            {step === 2
              ? 'Reply: 1 task. The service still works.'
              : 'A later client should still be able to connect.'}
          </p>
        </Panel>
      </div>
      <div className="cb-controls">
        <Advance disabled={step >= 2} onClick={() => setStep(step + 1)}>
          {step === 0 ? 'Handle first client' : 'Accept a valid next client'}
        </Advance>
        <Reset
          onClick={() => {
            setExample('incomplete');
            setStep(0);
          }}
        />
      </div>
      <Result>
        {step === 0
          ? 'This model handles one connection at a time. A size limit bounds memory; a receive timeout limits each wait for more bytes. A slow sender can still prolong the whole conversation.'
          : step === 1
            ? 'The handler ends this conversation and returns to accept. Timeouts and message limits do not require restarting the whole service.'
            : 'The next client receives a valid response because the listening socket survived the earlier failure.'}
      </Result>
    </Lab>
  );
}

export function HttpAgreementLab() {
  const [title, setTitle] = useState('Read notes');
  const [sent, setSent] = useState(false);
  const frame = privateAgreement(title);
  return (
    <Lab
      title="When a title contains the separator"
      eyebrow="Experiment · a shared agreement"
      className="nd-lab"
    >
      <Predict>
        Try the title “Read | review”. Can a receiver splitting every | still find exactly one title
        field?
      </Predict>
      <div className="cb-controls">
        <label>
          Task title
          <Input
            aria-label="Task title"
            value={title}
            maxLength={70}
            onChange={(event) => {
              setTitle(event.target.value);
              setSent(false);
            }}
          />
        </label>
        <Advance disabled={false} onClick={() => setSent(true)}>
          Interpret both messages
        </Advance>
        <Reset
          onClick={() => {
            setTitle('Read notes');
            setSent(false);
          }}
        />
      </div>
      <div className="nd-columns">
        <Panel label="Private delimiter format">
          <Output label="Private wire text">{frame.wire}</Output>
          <p>Rule: split at every |; expect two fields.</p>
          {sent ? <Output label="Receiver fields">{JSON.stringify(frame.fields)}</Output> : null}
        </Panel>
        <Panel label="HTTP request with a JSON body">
          <Output label="Structured message">{`POST /tasks HTTP/1.1\nContent-Type: application/json\nContent-Length: ${new TextEncoder().encode(frame.json).length}\n…other headers…\n\n${frame.json}`}</Output>
          <p>HTTP defines the request structure; JSON holds the title in its body.</p>
          {sent ? <Output label="Decoded title">{frame.title}</Output> : null}
        </Panel>
      </div>
      <Result tone={sent && !frame.valid ? 'warning' : 'neutral'}>
        {!sent
          ? 'The method and headers describe the request. The JSON body carries the title, including any | character inside it.'
          : !frame.valid
            ? 'The private rule cannot interpret this input as one nonblank title. It needs more rules. HTTP plus JSON already defines boundaries and escaping, but CollabBoard must still validate the title.'
            : 'Both representations preserve this title. Now put | inside it: the private splitter’s unstated assumption becomes visible.'}
      </Result>
    </Lab>
  );
}

export function HttpRequestLab() {
  const [method, setMethod] = useState('POST');
  const [path, setPath] = useState('/tasks');
  const [title, setTitle] = useState('Café review');
  const [part, setPart] = useState('method');
  const frame = requestFrame(method, path, title);
  const explanations: Record<string, string> = {
    method: `${method} expresses the requested operation. The path identifies its target; JSON does not select the operation.`,
    target: `The request target is ${path}. The scheme, host and port belong to the URL used to reach the server. A query is separate from the path. /tasks?completed=false still uses the /tasks route; this workshop returns all tasks. Filtering comes later.`,
    headers:
      'Content-Type describes this body’s representation. Accept describes response formats the client can understand. Neither replaces application validation.',
    body: frame.body
      ? `The JSON body contains title data. Its UTF-8 length is ${frame.bodyLength} bytes; that can differ from the number of text characters.`
      : 'This GET request has no body. The operation and target are sufficient for listing tasks.',
  };
  return (
    <Lab
      title="Separate the destination, operation and data"
      eyebrow="Experiment · inspect a request"
      className="nd-lab"
    >
      <Predict>
        Keep the same JSON but change the path. Should the server still create a task?
      </Predict>
      <div className="cb-controls">
        <Select
          label="HTTP method"
          value={method}
          onChange={setMethod}
          options={[
            { value: 'POST', label: 'POST · create' },
            { value: 'GET', label: 'GET · list' },
          ]}
        />
        <Select
          label="Request target"
          value={path}
          onChange={setPath}
          options={[
            { value: '/tasks', label: '/tasks' },
            { value: '/unknown', label: '/unknown' },
            { value: '/tasks?completed=false', label: '/tasks?completed=false' },
          ]}
        />
        <label>
          Title data
          <Input
            aria-label="Title data"
            value={title}
            disabled={method !== 'POST'}
            maxLength={70}
            onChange={(event) => setTitle(event.target.value)}
          />
        </label>
      </div>
      <div className="nd-url" aria-label="URL parts">
        <span>
          <small>Scheme</small>http://
        </span>
        <span>
          <small>Host</small>127.0.0.1
        </span>
        <span>
          <small>Port</small>:8000
        </span>
        <span>
          <small>Request target</small>
          {path}
        </span>
      </div>
      <div className="nd-inspector">
        <div className="nd-inspector-parts" aria-label="Request parts">
          {[
            { id: 'method', label: 'Method', value: method },
            { id: 'target', label: 'Target', value: path },
            { id: 'headers', label: 'Headers', value: 'Host · Accept · Content-Type' },
            { id: 'body', label: 'Body', value: frame.body || '(none)' },
          ].map((entry) => (
            <Button
              key={entry.id}
              variant="ghost"
              size="sm"
              aria-pressed={part === entry.id}
              onClick={() => setPart(entry.id)}
            >
              <small>{entry.label}</small>
              <code>{entry.value}</code>
            </Button>
          ))}
        </div>
        <Output label="Complete request · CRLF shown as line breaks">{frame.wire}</Output>
      </div>
      <Reset
        onClick={() => {
          setMethod('POST');
          setPath('/tasks');
          setTitle('Café review');
          setPart('method');
        }}
      />
      <Result>{explanations[part]}</Result>
    </Lab>
  );
}

export function HttpOutcomesLab() {
  const [example, setExample] = useState<OutcomeExample>('list');
  const [sent, setSent] = useState(false);
  const entry = outcomeExamples[example];
  return (
    <Lab
      title="A response explains what happened"
      eyebrow="Experiment · outcomes, not just text"
      className="nd-lab"
    >
      <Predict>
        Is an empty task collection a missing route? Is a refused connection an HTTP error response?
      </Predict>
      <div className="cb-controls">
        <Select
          label="Request scenario"
          value={example}
          onChange={(value) => {
            setExample(value as OutcomeExample);
            setSent(false);
          }}
          options={[
            { value: 'list', label: 'List an empty collection' },
            { value: 'create', label: 'Create a valid task' },
            { value: 'blank', label: 'Create a blank title' },
            { value: 'route', label: 'Use an unknown route' },
            { value: 'method', label: 'Use an unsupported method' },
            { value: 'offline', label: 'Connect with the listener stopped' },
          ]}
        />
        <Advance disabled={false} onClick={() => setSent(true)}>
          Observe this outcome
        </Advance>
        <Reset
          onClick={() => {
            setExample('list');
            setSent(false);
          }}
        />
      </div>
      <Output label="Client request">{entry.request}</Output>
      <div className="nd-columns">
        <Panel label="Response">
          <strong className="nd-status">
            {sent ? `${entry.code ?? 'No HTTP status'} · ${entry.name}` : 'Not observed yet'}
          </strong>
          {sent ? <Output label="Response body">{entry.body}</Output> : null}
          {sent && example === 'method' ? <code>Allow: GET, POST</code> : null}
        </Panel>
        <Panel label="Current data">
          <p>{sent ? entry.change : 'Each scenario begins with no tasks and next_id = 1.'}</p>
        </Panel>
      </div>
      <Result>
        {!sent
          ? 'Choose a scenario, predict its outcome, then observe the response and state together.'
          : example === 'offline'
            ? 'No connection means no HTTP conversation occurred. The client reports a connection failure, not a status such as 404.'
            : `${entry.code} is an HTTP outcome. ${example === 'list' ? 'An existing empty collection is a successful response: [] tells the client there are no records.' : 'The status and response body describe this request; inspect the current data as separate evidence.'}`}
      </Result>
    </Lab>
  );
}

export function HttpFramingLab() {
  const [title, setTitle] = useState('Café review');
  const [pattern, setPattern] = useState('headers');
  const [step, setStep] = useState(0);
  const frame = framingFrame(title, pattern, step);
  return (
    <Lab
      title="Read enough bytes before decoding"
      eyebrow="Experiment · HTTP message assembly"
      className="nd-lab"
      note="Use Content-Length to find the end of this request body, then compare the bytes with the parsed message."
    >
      <Predict>
        If the final header bytes and five body bytes arrive together, should those body bytes be
        thrown away?
      </Predict>
      <div className="cb-controls">
        <Select
          label="Delivery pattern"
          value={pattern}
          onChange={(value) => {
            setPattern(value);
            setStep(0);
          }}
          options={[
            { value: 'headers', label: 'Split the header terminator' },
            { value: 'together', label: 'Headers and body together' },
            {
              value: 'unicode',
              label:
                frame.bodyLength > frame.body.length
                  ? 'Split inside a UTF-8 character'
                  : 'Split body (no multibyte character)',
            },
          ]}
        />
        <label>
          Title to encode
          <Input
            aria-label="Title to encode"
            value={title}
            maxLength={40}
            onChange={(event) => {
              setTitle(event.target.value);
              setStep(0);
            }}
          />
        </label>
      </div>
      <div className="nd-meter" aria-label="Received bytes">
        <div>
          <span style={{ width: `${(frame.received / frame.bytes.length) * 100}%` }} />
        </div>
        <p>
          {frame.received} / {frame.bytes.length} total bytes received
        </p>
      </div>
      <div className="nd-columns">
        <Panel label="Header boundary">
          <Output label="End of headers">
            {frame.headerFound ? 'CRLF CRLF found' : 'Not found yet'}
          </Output>
          <p>
            {frame.headerFound
              ? `Content-Length says ${frame.bodyLength} body bytes.`
              : 'Keep buffering until the full header terminator appears.'}
          </p>
        </Panel>
        <Panel label="Body buffer">
          <p>
            <strong>{frame.bodyReceived}</strong> / {frame.headerFound ? frame.bodyLength : '?'}{' '}
            body bytes
          </p>
          <div className="nd-byte-rail" aria-label="Body bytes in hexadecimal">
            {Array.from(frame.bodyBytes).map((byte, index) => (
              <code key={index}>{byte.toString(16).toUpperCase().padStart(2, '0')}</code>
            ))}
          </div>
        </Panel>
      </div>
      <Output label="Safe application view">
        {frame.complete ? frame.body : '(body not yet complete — do not parse it)'}
      </Output>
      <div className="cb-controls">
        <Advance disabled={step >= frame.cuts.length} onClick={() => setStep(step + 1)}>
          Deliver next byte chunk
        </Advance>
        <Reset
          onClick={() => {
            setTitle('Café review');
            setPattern('headers');
            setStep(0);
          }}
        />
      </div>
      <Result tone={frame.decodeFailed ? 'warning' : frame.complete ? 'success' : 'neutral'}>
        {frame.complete
          ? 'The declared body is complete. Decode UTF-8 and parse JSON now. No need to wait for the client to close its connection.'
          : frame.decodeFailed
            ? 'The buffer ends inside a UTF-8 character. The final hexadecimal byte is only part of that character’s encoding. Decoding now would fail. Retain these bytes and receive the remainder.'
            : frame.headerFound
              ? 'Keep the body bytes that arrived with the headers. More body bytes are needed before parsing.'
              : 'A receive boundary has no special meaning to HTTP. The reader must locate the full header boundary first.'}
      </Result>
    </Lab>
  );
}

export function HttpValidationLab() {
  const [example, setExample] = useState<ValidationExample>('blank');
  const [step, setStep] = useState(0);
  const trace = validationTrace(example);
  const current = trace[step - 1];
  const input = validationExamples[example];
  return (
    <Lab
      title="Valid at one layer can fail at the next"
      eyebrow="Experiment · checks before changes"
      className="nd-lab"
    >
      <Predict>
        A body is valid JSON. Does that prove it describes a task CollabBoard can accept?
      </Predict>
      <div className="cb-controls">
        <Select
          label="Incoming example"
          value={example}
          onChange={(value) => {
            setExample(value as ValidationExample);
            setStep(0);
          }}
          options={[
            { value: 'http', label: 'Malformed request line' },
            { value: 'json', label: 'Unfinished JSON' },
            { value: 'list', label: 'JSON list instead of object' },
            { value: 'missing', label: 'Object without a title' },
            { value: 'blank', label: 'Title containing only spaces' },
            { value: 'valid', label: 'Valid task title' },
          ]}
        />
      </div>
      <Output label="Incoming request and body">{`${input.wire}\n…headers and boundary…\n${input.body}`}</Output>
      <ol className="nd-gates" aria-label="Validation stages">
        {['HTTP structure', 'JSON syntax', 'JSON object', 'Title field', 'Task rule'].map(
          (label, index) => {
            const gate = index < step ? trace[index] : undefined;
            return (
              <li key={label} data-outcome={gate ? (gate.pass ? 'passed' : 'rejected') : 'waiting'}>
                <span>{index + 1}</span>
                <strong>{label}</strong>
                <small>{gate ? (gate.pass ? 'Passed' : 'Rejected here') : 'Not reached'}</small>
                {gate ? <p>{gate.detail}</p> : null}
              </li>
            );
          },
        )}
      </ol>
      <Output label="Current data">No tasks · next_id = 1</Output>
      <div className="cb-controls">
        <Advance disabled={step >= trace.length} onClick={() => setStep(step + 1)}>
          Inspect next layer
        </Advance>
        <Reset
          onClick={() => {
            setExample('blank');
            setStep(0);
          }}
        />
      </div>
      <Result tone={current && !current.pass ? 'warning' : 'neutral'}>
        {!current
          ? 'No application state has changed. Move through the checks in order.'
          : !current.pass
            ? `${current.detail} Later stages do not run, and no ID is consumed.`
            : step === trace.length
              ? 'The request passed validation. The server can now prepare a working copy with the new task. Validation alone has not saved or accepted a task.'
              : `${current.detail} This success does not prove the next condition.`}
      </Result>
    </Lab>
  );
}

export function HttpCommitLab() {
  const [save, setSave] = useState('success');
  const [reply, setReply] = useState('success');
  const [step, setStep] = useState(0);
  const frame = httpCommitFrame(step, save === 'failure', reply === 'failure');
  const labels = [
    'Prepare working copy',
    'Try saving',
    save === 'failure' ? 'Keep current data' : 'Use saved copy',
    'Send response',
  ];
  const select = (setter: (value: string) => void) => (value: string) => {
    setter(value);
    setStep(0);
  };
  return (
    <Lab
      title="Saved does not always mean confirmed"
      eyebrow="Experiment · commit and reply"
      className="nd-lab"
      note="Follow one POST through validation, saving and response. Compare a failed save with a lost response after a successful save."
    >
      <Predict>
        Suppose saving succeeds but the client receives no reply. Does the task exist? What does the
        client know?
      </Predict>
      <div className="cb-controls">
        <Select
          label="Save outcome"
          value={save}
          onChange={select(setSave)}
          options={[
            { value: 'success', label: 'Save succeeds' },
            { value: 'failure', label: 'Save fails before replacement' },
          ]}
        />
        <Select
          label="Response delivery"
          value={reply}
          onChange={select(setReply)}
          options={[
            { value: 'success', label: 'Client receives the reply' },
            { value: 'failure', label: 'Client receives no reply' },
          ]}
        />
      </div>
      <Progress labels={labels} step={step} />
      <div className="nd-columns nd-three">
        <Panel label="Working copy · proposed change">
          <Output label="Proposed task">
            {frame.candidate ? '#1 · Café review' : '(not prepared)'}
          </Output>
        </Panel>
        <Panel label="Saved file">
          <Output label="Stored task">{frame.disk ? '#1 · Café review' : '(no tasks)'}</Output>
        </Panel>
        <Panel label="Current data in memory">
          <Output label="Accepted task">
            {frame.accepted ? '#1 · Café review' : '(no tasks)'}
          </Output>
          <p>next_id = {frame.nextId}</p>
        </Panel>
      </div>
      <Output label="Client’s evidence">{frame.client}</Output>
      <div className="cb-controls">
        <Advance disabled={step >= 4} onClick={() => setStep(step + 1)}>
          {step === 2 && save === 'failure'
            ? 'Keep current data'
            : (labels[step] ?? 'Request complete')}
        </Advance>
        <Reset
          onClick={() => {
            setSave('success');
            setReply('success');
            setStep(0);
          }}
        />
      </div>
      <Result
        tone={
          step >= 2 && (save === 'failure' || (reply === 'failure' && step >= 4))
            ? 'warning'
            : 'neutral'
        }
      >
        {frame.explanation}
      </Result>
    </Lab>
  );
}
